import { AppContext, AppProps } from 'next/app';
import Router from 'next/router';
import React, { FC, useEffect, useState } from 'react';
import TagManager from 'react-gtm-module';

import { SpacesProvider } from '@ably/spaces/react';
import { Box, Loader, MantineProvider, v8CssVariablesResolver } from '@mantine/core';
import { useWindowEvent } from '@mantine/hooks';
import { ModalsProvider } from '@mantine/modals';
import { Notifications } from '@mantine/notifications';
import * as Sentry from '@sentry/browser';
import { Analytics, BeforeSendEvent } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { AblyProvider } from 'ably/react';
import { SWRConfig } from 'swr';

import { publicConfig } from '@public-config';

import { CompanyProfile, PageLoadGlobals, Region, Team, TeamInfo, User } from 'models';

import { Notifications as LocalNotifications } from 'components/notifications/Notifications';
import { Spotlight } from 'components/spotlight/Spotlight';

// import driver styles before mentine styles
import 'driver.js/dist/driver.css';

import '@mantine/core/styles.css';
import '@mantine/tiptap/styles.css';
import '@mantine/carousel/styles.css';
import '@mantine/charts/styles.css';
import '@mantine/spotlight/styles.css';
import '../public/global-styles.scss';
import '../public/animations.css';
import '../public/diagramStyles.css';

import { Geo } from '@vercel/functions';
import { SessionStorageService } from 'src/frontend/services/SessionStorageService';

import { GEOHelpers } from 'helpers/GEOHelpers';
import { getRequestUser } from 'helpers/get-request-user';
import { getTouchDeviceType } from 'helpers/isTouchDevice';
import { RequestHelpers } from 'helpers/RequestHelpers';
import { WorkspaceHelpers } from 'helpers/WorkspaceHelpers';

import { ImpersonationBadge } from 'components/badges/ImpersonationBadge';
import { GlobalContextMenu } from 'components/context-menu/GlobalContextMenu';
import { SuggestEventDialog } from 'components/dialogs/SuggestEventDialog';
import { ErrorBoundary } from 'components/error-boundary/ErrorBoundary';
import { MaintenancePageWrapper } from 'components/MaintenancePage';
import { modals } from 'components/modals';
import { PageDataAttributes } from 'components/PageDataAttributes';
import { Metadata } from 'components/page/Metadata';
import { RealtimeTeamConnector } from 'components/realtime-user/RealtimeTeamConnector';
import { RealtimeUserProvider } from 'components/realtime-user/RealtimeUserProvider';
import { CurrentAppTracking } from 'components/setup/CurrentAppTracking';
import { LocalStorageTracking } from 'components/setup/LocalStorageTracking';
import { ScanNotesSheetHost } from 'components/showtime/components/ScanNotesSheetHost';
import { GlobalSubscriptionModal } from 'components/subscriptions/components/GlobalSubscriptionModal';
import { UserSuggestions } from 'components/user-suggestions/UserSuggestions';
import { SwitchWorkspaceNotice } from 'components/workspace/SwitchWorkspaceNotice';
import { useScrollSetter } from 'hooks/dom/use-scroll';
import { useViewportSizeSetter } from 'hooks/dom/use-viewport-size';
import { useActivityHeartbeat } from 'hooks/use-activity-heartbeat';
import { useAppEffects } from 'hooks/use-app-effects';
import { useGlobalState } from 'hooks/use-global-state';
import { useHeartbeat } from 'hooks/use-heartbeat';
import { useSnapshot } from 'hooks/use-safe-snapshot';
import { useSaveItemFromQuery } from 'hooks/use-save-item-from-query';
import { useSetActiveEventOnMount } from 'hooks/use-set-active-event';
import { useSetShowShowtime } from 'hooks/use-set-show-showtime';
import { useWorkspaceTheme } from 'hooks/use-workspace-theme';
import { AuthenticationService } from 'services/AuthenticationService';
import { CompanyProfileService } from 'services/CompanyProfileService';
import { InternalTrackingService } from 'services/InternalTrackingService';
import { MeasurementSystemService } from 'services/MeasurementSystemService';
import { RealtimeService } from 'services/RealtimeService';
import { TeamService } from 'services/TeamService';
// Don't move this above the styles, it gets funky.
import { UserService } from 'services/UserService';
import { WorkspaceService } from 'services/WorkspaceService';
import { state as currentTeamState } from 'state/current-team';
import { currentTeamCompaniesState } from 'state/current-team-companies';
import { state as currentTeamsState } from 'state/current-teams';
import { state as currentUserState } from 'state/current-user';
import { state as defaultRegionState } from 'state/default-region';
import { geoState } from 'state/geo';
import { loadingState } from 'state/loading';
import { pageLoadGlobalsState } from 'state/page-load-globals';
import { WorkspaceConfigState, workspaceConfigState } from 'state/workspace-config';

declare module '@mantine/modals' {
    // @ts-ignore
    export interface MantineModalsOverride {
        modals: typeof modals;
    }
}

// On a workspace host the browser must call the API same-origin: the auth cookie is
// scoped per host, so cross-site requests to the main domain would neither send
// nor receive it (login would never stick). The same deployment serves /api on
// every domain, so rebasing to the current origin is safe. The frontend URL is
// rebased too so shareable links (e.g. team invite links) stay on the workspace
// host, mirroring WorkspaceProvider for the app router.
if (typeof window !== 'undefined' && WorkspaceHelpers.isWorkspace(window.location.host)) {
    publicConfig.urls.api = `${window.location.origin}/api`;
    publicConfig.urls.frontend = window.location.origin;
}

type ExtendedAppProps = {
    user: User | null;
    team: Team | null;
    teams: TeamInfo[];
    currentTeamCompanies: CompanyProfile[];
    geo: Geo;
    defaultRegion: Region | null;
    workspaceConfig: WorkspaceConfigState;
    pageLoadGlobals: PageLoadGlobals | null;
};

const App = ({
    Component,
    pageProps,
    user,
    team,
    teams,
    currentTeamCompanies,
    geo,
    defaultRegion,
    workspaceConfig,
    pageLoadGlobals,
}: AppProps & ExtendedAppProps) => {
    const initial = pageProps?.initial || {};

    useAppEffects(initial.user || user);

    useGlobalState(currentUserState, 'user', initial.user || user);
    useGlobalState(currentTeamState, 'team', initial.team || team);
    useGlobalState(currentTeamsState, 'teams', initial.teams || teams);
    useGlobalState(currentTeamCompaniesState, 'companies', initial.currentTeamCompanies || currentTeamCompanies);

    useGlobalState(geoState, 'geo', geo);
    useGlobalState(defaultRegionState, 'defaultRegion', initial.defaultRegion || defaultRegion);
    useGlobalState(workspaceConfigState, 'config', workspaceConfig);

    useGlobalState(pageLoadGlobalsState, 'globals', pageLoadGlobals);

    const [render, setRender] = useState(false);

    useEffect(() => {
        setRender(true);
    }, []);

    useEffect(() => {
        const switchTeam = Router.query.switchTeam as string;

        if (!switchTeam || !currentUserState.user) return;

        UserService.switchTeam(switchTeam).then((result) => {
            const updatedQuery = Router.query;
            delete updatedQuery.switchTeam;

            Router.replace(
                {
                    query: updatedQuery,
                },
                undefined,
                { shallow: !result?.success },
            ).then();
        });
    }, [currentTeamState.team, currentUserState.user]);

    return (
        <>
            <ConfigWrapper>
                <Metadata
                    title={pageProps?.metadata?.title}
                    description={pageProps?.metadata?.description}
                    image={pageProps?.metadata?.image}
                />
                {render ? (
                    <ErrorBoundary onError={() => InternalTrackingService.track('error.boundary.app')}>
                        <Effects />
                        <RealtimeWrapper team={team} user={user}>
                            <MaintenancePageWrapper>
                                <ModalsProvider modals={modals}>
                                    <PageDataAttributes />
                                    <Notifications position="bottom-center" zIndex={1000} />
                                    <ImpersonationBadge />
                                    <SwitchWorkspaceNotice />
                                    <LocalNotifications />
                                    {currentUserState.user && <UserSuggestions />}
                                    <Spotlight />
                                    <GlobalContextMenu />
                                    <GlobalSubscriptionModal />
                                    <SuggestEventDialog />
                                    <ScanNotesSheetHost />
                                    <Component {...pageProps} />
                                </ModalsProvider>
                            </MaintenancePageWrapper>
                        </RealtimeWrapper>
                    </ErrorBoundary>
                ) : (
                    <Box
                        style={{
                            width: '100vw',
                            height: '100vh',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <Loader c="blue" />
                    </Box>
                )}
            </ConfigWrapper>
            <SpeedInsights />
            {!user?.internal && !user?.developer && (
                <Analytics
                    beforeSend={(event: BeforeSendEvent) => {
                        if (
                            event.url.includes('/dispatcher') ||
                            event.url.includes('/tracker') ||
                            event.url.includes('/404') ||
                            /\/inbox\/[^/]+\/read/.test(event.url)
                        ) {
                            return null;
                        }
                        return event;
                    }}
                />
            )}
        </>
    );
};

const Effects = () => {
    useHeartbeat();
    useActivityHeartbeat();
    useSetActiveEventOnMount();
    useScrollSetter();
    useViewportSizeSetter();
    useSaveItemFromQuery();
    useSetShowShowtime();

    useEffect(() => {
        const touchType = getTouchDeviceType();

        document.body.setAttribute(`data-touch-device`, touchType);
    });

    useEffect(() => {
        if (currentUserState.user?.measurementSystem) {
            MeasurementSystemService.set(currentUserState.user.measurementSystem);
        }
    }, []);

    useSentrySetup();
    useTagManagerSetup();
    useInternalTracking();
    useRouterEventsSetup();

    return (
        <>
            <LocalStorageTracking />
            <CurrentAppTracking />
        </>
    );
};

const useSentrySetup = () => {
    const { user } = useSnapshot(currentUserState);

    useEffect(() => {
        if (!publicConfig.sentryPublicKey) return;

        const goofy = user?.goofy || AuthenticationService.getFallbackGoofy();

        Sentry.setUser({ username: user?.internal ? `${goofy} (Internal)` : goofy });
        Sentry.setTag('internal', user?.internal ?? false);
    }, [user?.goofy, user?.internal]);
};

const useTagManagerSetup = () => {
    useEffect(() => {
        if (!publicConfig.analyticsPublicKey) return;

        TagManager.initialize({
            gtmId: publicConfig.analyticsPublicKey,
            dataLayer: {
                event: 'page_view',
            },
        });
    }, []);
};

const useInternalTracking = () => {
    useEffect(() => {
        InternalTrackingService.start();
        InternalTrackingService.startSession();
        InternalTrackingService.track('page.view');

        Router.events.on('routeChangeStart', () => {
            SessionStorageService.store('tracking-referrer', window.location.toString());
        });

        Router.events.on('routeChangeComplete', () => {
            UserService.ping();
            InternalTrackingService.track('page.view');
        });
    }, []);

    useWindowEvent('beforeunload', () => {
        SessionStorageService.store('tracking-referrer', window.location.toString());
        UserService.ping();
    });

    useWindowEvent('visibilitychange', () => {
        if (document.hidden) {
            UserService.ping();
        }
    });
};

const useRouterEventsSetup = () => {
    useEffect(() => {
        const start = (destination: string) => {
            loadingState.page = true;
            loadingState.destination = destination;
        };

        const settle = () => {
            loadingState.page = false;
            loadingState.destination = null;
        };

        Router.events.on('routeChangeStart', start);
        Router.events.on('routeChangeComplete', settle);
        Router.events.on('routeChangeError', settle);

        return () => {
            Router.events.off('routeChangeStart', start);
            Router.events.off('routeChangeComplete', settle);
            Router.events.off('routeChangeError', settle);
        };
    }, []);
};

const ConfigWrapper: FC<{ children: React.ReactNode }> = ({ children }) => {
    const mergedTheme = useWorkspaceTheme();

    return (
        <MantineProvider theme={mergedTheme} cssVariablesResolver={v8CssVariablesResolver}>
            <SWRConfig value={{ revalidateIfStale: false }}>{children}</SWRConfig>
        </MantineProvider>
    );
};

const RealtimeWrapper: FC<{
    team: Team | null;
    user: User | null;
    children: React.ReactNode;
}> = ({ team, user, children }) => {
    if (typeof window === 'undefined' || !user) {
        return <>{children}</>;
    }

    const { ably, spaces } = RealtimeService.init(user);

    return (
        <AblyProvider client={ably}>
            <SpacesProvider client={spaces}>
                {team && <RealtimeTeamConnector team={team} user={user} />}
                <RealtimeUserProvider user={user}>{children}</RealtimeUserProvider>
            </SpacesProvider>
        </AblyProvider>
    );
};

App.getInitialProps = async (context: AppContext): Promise<ExtendedAppProps> => {
    const { req, res } = context.ctx;

    const initialProps: ExtendedAppProps = {
        user: null,
        team: null,
        currentTeamCompanies: [],
        teams: [],
        geo: {},
        defaultRegion: null,
        workspaceConfig: {},
        pageLoadGlobals: null,
    };

    if (!req || !res) {
        if (typeof window !== 'undefined') {
            // Client-side navigation: what loaded on the initial server render is still current.
            initialProps.workspaceConfig = workspaceConfigState.config;
            initialProps.pageLoadGlobals = pageLoadGlobalsState.globals;
        }

        return initialProps;
    }

    const fetchWorkspaceConfig = async () => {
        initialProps.workspaceConfig = (await WorkspaceService.getRequestConfig(req)) ?? {};
    };

    // Started here and awaited alongside the user and team fetches so it does not delay them.
    const workspaceConfigLoaded = fetchWorkspaceConfig();

    if (req.headers['x-geo']) {
        const geo = RequestHelpers.decode(req.headers['x-geo'] as string);

        initialProps.geo = geo;
        initialProps.defaultRegion = GEOHelpers.getRegionFromVercelRegion(geo.region ?? '');
    }

    const fetchPageLoadGlobals = async () => {
        try {
            const response = await fetch(`${publicConfig.urls.api}/globals/general/page-load`, {
                headers: publicConfig.environmentLockAccess
                    ? { environmentLockAccess: publicConfig.environmentLockAccess }
                    : undefined,
            });
            const general: PageLoadGlobals = await response.json();

            initialProps.pageLoadGlobals = general;
        } catch {
            // silently fail — these are optional decorations on every page
        }
    };

    const user = await getRequestUser(req);

    if (!user) {
        await Promise.all([fetchPageLoadGlobals(), workspaceConfigLoaded]);

        return initialProps;
    }

    initialProps.user = user;

    const fetchTeam = async () => {
        const teams = await TeamService.getAvailableTeams(req);

        initialProps.team = (teams || []).find((team) => team.active) || null;
        initialProps.teams = teams;
    };

    const fetchCurrentTeamCompanies = async () => {
        const teamId = initialProps.user?.team;

        if (teamId) {
            const { docs: companies } = await CompanyProfileService.getByTeam(teamId, 1, req);

            initialProps.currentTeamCompanies = companies;
        }
    };

    await Promise.all([fetchTeam(), fetchCurrentTeamCompanies(), fetchPageLoadGlobals(), workspaceConfigLoaded]);

    return initialProps;
};

export default App;
